import "./ViewOrEditForm.scss";
import { formSubmitWithWaitForJobCompletion, JobDetail } from '../../scripts/jobCompletion';
import { initializeTooltips } from '../../scripts/tooltips';
import { disableElementAndAddSpinner } from "../../scripts/jobCompletion";
import { FormDataSaver } from "../../scripts/formDataSaver";
import { translate } from "../../scripts/translate";
import * as TranslationsKeys from "../../scripts/locale/translationsKeys";
import { dialogConfirmationButtonClassName, dialogCancelButtonClassName } from "../../scripts/dialogConstants";
import { trackEvent } from "../../Features/Analytics";
import ComponentControllerBase from "../../Features/ComponentControllerBase";

export const formSubmittedEventName = "formSubmitted";
export const formEditingCancelledEventName = "formEditingCancelled";
export const viewOrEditFormConnectedEventName = "viewOrEditFormConnected";

export default class ViewOrEditFormController extends ComponentControllerBase {
    static targets = [
        "form",
        "edit",
        "save",
        "cancel",
        "viewModeItem",
        "editModeItem",
        "isValidationActive",
        "unsavedChanges"
    ];

    static values = {
        ...ComponentControllerBase.values,
        startInViewMode: Boolean,
    }

    readonly formTarget!: HTMLElement;
    readonly editTarget!: HTMLElement;
    readonly hasEditTarget!: boolean;
    readonly saveTargets!: HTMLElement[];
    readonly cancelTargets!: HTMLElement[];
    readonly viewModeItemTargets!: HTMLElement[];
    readonly editModeItemTargets!: HTMLElement[];
    readonly hasIsValidationActiveTarget!: boolean;
    readonly isValidationActiveTarget!: HTMLElement;
    readonly unsavedChangesTarget!: HTMLElement;

    startInViewModeValue!: boolean;
    formDataSaver!: FormDataSaver;

    isEditing!: boolean;

    connect() {
        const form = $(this.formTarget);
        initializeTooltips(form);

        if (!this.hasEditTarget) return;

        this.switchToViewMode();
        this.attachOnChangeForIsValidationActiveCheckbox();

        const shouldEnableDataValidationFormParameterName = this.hasIsValidationActiveTarget
            ? $(this.isValidationActiveTarget).attr("name")
            : undefined;

        this.formDataSaver = new FormDataSaver(this.formTarget as HTMLFormElement);

        formSubmitWithWaitForJobCompletion(
            form,
            $(this.saveTargets),
            this.getJobUrlValue,
            (job) => {
                this.formDataSaver.stopPeriodicFormDataSaving();
                this.formDataSaver.clearSavedFormData();
                this.raiseEventFormSubmitted(job);
                if (this.analyticsContextValue) {
                    trackEvent(`${this.analyticsContextValue}Saved`);
                }
            },
            undefined,
            shouldEnableDataValidationFormParameterName,
            false
        );

        if (this.formDataSaver.hasSavedFormData()) {
            this.formDataSaver.loadSavedFormData();
            $(this.unsavedChangesTarget).show();
            this.startInViewModeValue = false;
        }

        if (!this.startInViewModeValue) {
            this.edit();
        }

        this.element.dispatchEvent(new Event(viewOrEditFormConnectedEventName, { bubbles: true }));
    }

    disconnect() {
        this.formDataSaver?.stopPeriodicFormDataSaving();
    }

    private raiseEventFormSubmitted(job: JobDetail) {
        this.formTarget.dispatchEvent(new CustomEvent(formSubmittedEventName, { bubbles: true, detail: job }));
    }

    edit() {
        if (this.isEditing) return;

        this.isEditing = true;

        this.enableEagerValidation();
        $(this.editTarget).hide();
        $(this.saveTargets).show();
        $(this.cancelTargets).show();
        this.viewModeItemTargets.forEach(i => $(i).hide());
        this.editModeItemTargets.forEach(i => $(i).show());
        this.formDataSaver.setupPeriodicFormDataSaving();

        if (this.analyticsContextValue) {
            trackEvent(`${this.analyticsContextValue}Edit`);
        }
    }

    enableEagerValidation() {
        const form = $(this.formTarget);
        const validator = form.data("validator");
        if (!validator) return;

        const formValidatorSettings = validator.settings;
        formValidatorSettings.onfocusout = element => {
            $(element).valid();
        };
        formValidatorSettings.onkeyup = element => {
            $(element).valid();
        };
        form.valid();
    }

    cancel() {
        this.isEditing = false;

        bootbox.confirm({
            message: translate(TranslationsKeys.Global_CancelEditing_Message),
            buttons: {
                confirm: {
                    label: translate(TranslationsKeys.Global_Ok),
                    className: dialogConfirmationButtonClassName
                },
                cancel: {
                    label: translate(TranslationsKeys.Global_Cancel),
                    className: dialogCancelButtonClassName
                }
            },
            callback: (result) => {
                if (!result) return;

                this.formDataSaver.stopPeriodicFormDataSaving();
                this.formDataSaver.clearSavedFormData();

                disableElementAndAddSpinner($(this.cancelTargets));
                this.raiseEventFormEditingCancelled();

                if (this.analyticsContextValue) {
                    trackEvent(`${this.analyticsContextValue}Canceled`);
                }
            }
        });
    }

    private raiseEventFormEditingCancelled() {
        this.formTarget.dispatchEvent(new Event(formEditingCancelledEventName, { bubbles: true }));
    }

    switchToViewMode() {
        $(this.editTarget).show();
        $(this.saveTargets).hide();
        $(this.cancelTargets).hide();
        $(this.unsavedChangesTarget).hide();
        this.viewModeItemTargets.forEach(i => $(i).show());
        this.editModeItemTargets.forEach(i => $(i).hide());
    }

    private attachOnChangeForIsValidationActiveCheckbox() {
        if (!this.hasIsValidationActiveTarget) return;

        const isValidationActiveCheckbox = $(this.isValidationActiveTarget);

        this.refreshValidationStyle(isValidationActiveCheckbox);

        isValidationActiveCheckbox.on("change", () => {
            this.refreshValidationStyle(isValidationActiveCheckbox);
        });

    }

    private refreshValidationStyle(isValidationActiveCheckbox: JQuery<HTMLElement>) {
        const form = $(this.formTarget);
        const errorAsWarningControls = form.find(".error-as-warning-control");
        if (isValidationActiveCheckbox.is(":checked")) {
            errorAsWarningControls.removeClass("error-as-warning");
        }
        else {
            errorAsWarningControls.addClass("error-as-warning");
        }
    }
}
